// The one validated seam between the stringly-typed UI (a <select> emits `string`, ShapeValue emits
// the wide ConditionValue) and the predicate model. Each constructor validates the operator against
// the column type's operator set and coerces the value to that operator's shape. The `type` comes
// from the leaf's Row (it is no longer carried on the predicate).

import type { AttributePredicate, AttributeType, ConditionValue, OperatorValue, ValueShape } from '../types';
import { ATTRIBUTE_OPERATORS, findOperator, operatorShape, seedValueForShape } from './operators';

/** A partial edit merged from an insight pick — kept wide so this stays below the insights layer. */
export interface AttributePatchInput {
  operator?: OperatorValue;
  value?: ConditionValue;
}

/** Change an attribute predicate's operator, re-seeding its value to the new operator's shape. */
export function withAttributeOperator(
  predicate: AttributePredicate,
  type: AttributeType,
  operator: OperatorValue,
): AttributePredicate {
  const operators = ATTRIBUTE_OPERATORS[type];
  if (!findOperator(operators, operator)) return predicate;
  const value = seedValueForShape(
    operatorShape(operators, operator),
    predicate.value ?? null,
    operatorShape(operators, predicate.operator),
  );
  return { ...predicate, operator, value };
}

export function withAttributeValue(predicate: AttributePredicate, value: ConditionValue): AttributePredicate {
  return { ...predicate, value };
}

/** Normalise a value to the operator's shape at the value level — an insight can send a scalar where the
 *  operator wants a list (a single pill pick under `in` / "is one of") or vice-versa. Only the scalar↔list
 *  mismatch is fixed here; range / relative-window / none values are authored with the right shape. */
function coerceValueToShape(value: ConditionValue, shape: ValueShape): ConditionValue {
  switch (shape) {
    case 'multi_enum':
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return [value];
      if (typeof value === 'number') return [value];
      return [];
    case 'single_enum':
    case 'text':
    case 'number':
    case 'date':
    case 'boolean':
      return Array.isArray(value) ? (value[0] ?? null) : value;
    case 'none':
    case 'number_range':
    case 'date_range':
    case 'relative_window':
    default:
      return value;
  }
}

/** Merge an insight patch into an attribute predicate — validates the operator against the type. */
export function withAttributePatch(
  predicate: AttributePredicate,
  type: AttributeType,
  patch: AttributePatchInput,
): AttributePredicate {
  const operators = ATTRIBUTE_OPERATORS[type];
  const nextOperator = patch.operator ?? predicate.operator;
  if (!findOperator(operators, nextOperator)) return predicate;

  let next = predicate;
  if (patch.operator !== undefined && patch.operator !== predicate.operator) {
    next = withAttributeOperator(next, type, patch.operator);
  }
  if (patch.value !== undefined) {
    // Coerce to the FINAL operator's shape so a scalar can't land under a list operator (unrepresentable state).
    next = withAttributeValue(next, coerceValueToShape(patch.value, operatorShape(operators, nextOperator)));
  }
  return next;
}
